// src/components/IdleLogoutGuard.jsx
import React, { useEffect } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { onIdTokenChanged, signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';

/**
 * Props:
 * - timeoutMin: minutos de inatividade até sair (default 30)
 * - warningSec: segundos de aviso antes do logout (0 para desativar aviso)
 * - onLogout?: callback opcional (default: signOut(auth))
 * - redirectTo?: path para redirecionar após logout (ex: "/login")
 */
const IdleLogoutGuard = ({
  children,
  timeoutMin = 30,
  warningSec = 60,
  onLogout,
  redirectTo,
}) => {
  useEffect(() => {
    const TIMEOUT_MS = Math.max(1, timeoutMin) * 60_000;

    // garante que o aviso nunca seja >= timeout (senão avisa imediatamente e pode confundir)
    const SAFE_WARNING_MS = Math.min(
      Math.max(0, warningSec * 1000),
      Math.max(0, TIMEOUT_MS - 1000)
    );

    const LS_LAST_ACTIVITY = 'gp.lastActivityAt';
    const LS_LOGOUT_AT = 'gp.logoutAt';

    let warnTimer = null;
    let logoutTimer = null;
    let swalTickInterval = null;
    let heartbeatInterval = null;
    let warnOpen = false;
    let lastActivityWrite = 0; // evita escrever em excesso no localStorage

    const clearTimers = () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      warnTimer = null;
      logoutTimer = null;
    };

    const getLogoutAt = () => Number(localStorage.getItem(LS_LOGOUT_AT) || 0);
    const getRemainingMs = () => Math.max(0, getLogoutAt() - Date.now());

    const doLogout = async () => {
      clearTimers();
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      try {
        if (typeof onLogout === 'function') {
          await onLogout();
        } else {
          await signOut(auth);
        }
      } catch (_) {
        // noop
      } finally {
        if (redirectTo) {
          window.location.replace(redirectTo);
        }
      }
    };

    const scheduleFrom = (logoutAt) => {
      clearTimers();
      const now = Date.now();
      // importante: clamp em relação a "now" (não 0) para evitar horários no passado
      const warnAt = Math.max(now, logoutAt - SAFE_WARNING_MS);

      const warnIn = Math.max(0, warnAt - now);
      const logoutIn = Math.max(0, logoutAt - now);

      if (SAFE_WARNING_MS > 0 && logoutIn > 0) {
        warnTimer = setTimeout(showWarning, warnIn);
      }
      logoutTimer = setTimeout(doLogout, logoutIn);

      // Heartbeat de segurança: a cada 1s verifica se já passou do horário
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      heartbeatInterval = setInterval(() => {
        const remaining = getRemainingMs();
        if (remaining <= 0) {
          doLogout();
        }
      }, 1000);
    };

    const registerActivity = () => {
      const now = Date.now();
      // grava no máximo 1x por segundo para evitar flood no localStorage
      if (now - lastActivityWrite < 1000) return;
      lastActivityWrite = now;

      localStorage.setItem(LS_LAST_ACTIVITY, String(now));
      const logoutAt = now + TIMEOUT_MS;
      localStorage.setItem(LS_LOGOUT_AT, String(logoutAt));
      scheduleFrom(logoutAt);
    };

    const showWarning = () => {
      if (warnOpen || SAFE_WARNING_MS <= 0) return;
      warnOpen = true;

      const html = `
        Você será desconectado por inatividade em
        <b><span id="idle-count">${Math.ceil(getRemainingMs() / 1000)}</span>s</b>.
      `;

      Swal.fire({
        title: 'Sessão inativa',
        html,
        icon: 'warning',
        allowOutsideClick: true,
        allowEscapeKey: true,
        showCancelButton: true,
        confirmButtonText: 'Continuar sessão',
        cancelButtonText: 'Sair agora',
        reverseButtons: true,
        didOpen: () => {
          const span = document.getElementById('idle-count');
          swalTickInterval = setInterval(() => {
            if (!span) return;
            span.textContent = String(Math.ceil(getRemainingMs() / 1000));
          }, 250);
        },
        willClose: () => {
          if (swalTickInterval) clearInterval(swalTickInterval);
          swalTickInterval = null;
          warnOpen = false;
        },
      }).then((res) => {
        if (res.isConfirmed) {
          registerActivity(); // renova sessão
        } else if (res.dismiss === Swal.DismissReason.cancel) {
          doLogout();
        }
      });
    };

    const onActivity = (e) => {
      // Em 'visibilitychange', só registra quando a aba volta a ficar visível
      if (e.type === 'visibilitychange') {
        if (!document.hidden) registerActivity();
      } else {
        registerActivity();
      }
    };

    const onStorage = (e) => {
      if (e.key === LS_LAST_ACTIVITY || e.key === LS_LOGOUT_AT) {
        const logoutAt = getLogoutAt();
        if (logoutAt) scheduleFrom(logoutAt);
      }
    };

    // Eventos de atividade (menos ruidosos que 'mousemove' contínuo)
    const winEvents = ['focus'];
    const docEvents = [
      'visibilitychange',
      'pointerdown',
      'keydown',
      'wheel',
      'touchstart',
      'scroll',
    ];

    winEvents.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true })
    );
    docEvents.forEach((ev) =>
      document.addEventListener(ev, onActivity, { passive: true })
    );
    window.addEventListener('storage', onStorage);

    const unsubAuth = onIdTokenChanged(auth, (user) => {
      clearTimers();
      if (heartbeatInterval) clearInterval(heartbeatInterval);

      if (user) {
        // Se já existe um horário de logout salvo (ex.: outra aba), usa-o; senão registra agora
        const saved = getLogoutAt();
        if (saved && saved > Date.now()) {
          scheduleFrom(saved);
        } else {
          registerActivity();
        }
      } else {
        // sem usuário, nada a fazer
      }
    });

    // inicializa se já logado
    if (auth.currentUser) {
      const saved = getLogoutAt();
      if (saved && saved > Date.now()) {
        scheduleFrom(saved);
      } else {
        registerActivity();
      }
    }

    return () => {
      clearTimers();
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (swalTickInterval) clearInterval(swalTickInterval);
      winEvents.forEach((ev) => window.removeEventListener(ev, onActivity));
      docEvents.forEach((ev) => document.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      unsubAuth && unsubAuth();
    };
  }, [timeoutMin, warningSec, onLogout, redirectTo]);

  return <>{children}</>;
};

export default IdleLogoutGuard;
